export interface ServiceArea {
  slug: string;
  city: string;
  state: string;
  county: string;
  metaTitle: string;
  metaDescription: string;
  keywords: string[];
  heroHeading: string;
  heroSubheading: string;
  localIntro: string;         // 2–3 sentences unique to this city
  proximityNote: string;      // how close to Temecula / pickup context
  population: string;
  driveMinutes: string;       // approx drive from city to Temecula pickup
  nearbyLandmarks: string[];  // local reference points for relevance
  nearbyCities: string[];     // other cities we serve — linked in page
  lat: number;
  lng: number;
  googleBusinessUrl?: string; // Google Business Profile link
}

export const SERVICE_AREAS: ServiceArea[] = [
  {
    slug: 'temecula',
    city: 'Temecula',
    state: 'CA',
    county: 'Riverside County',
    metaTitle: 'Custom PS5 Controllers & Repair in Temecula, CA | GameFix Solutions',
    metaDescription:
      'Temecula\'s source for custom PS5 DualSense controllers, TMR hall-effect joystick upgrades, stick drift repair, mouse click triggers, and back paddles. Local pickup by appointment. Nationwide mail-in available.',
    keywords: [
      'ps5 repair temecula',
      'custom ps5 controller temecula',
      'ps5 stick drift repair temecula',
      'temecula gaming controller upgrade',
      'dualsense repair temecula ca',
      'ps5 controller temecula',
      'tmr joystick upgrade temecula',
    ],
    heroHeading: 'Custom PS5 Controllers & Repair in Temecula, CA',
    heroSubheading:
      'Temecula\'s go-to for TMR joystick upgrades, mouse click triggers, custom DualSense builds, and stick drift repair. Local pickup by appointment — or mail it in from anywhere.',
    localIntro:
      'GameFix Solutions is based in the Temecula area, making us the closest professional PS5 controller modification shop for players throughout Southwest Riverside County. Whether you\'re in the Redhawk, Meadowview, or Harveston neighborhoods, we\'re just a short drive away. We work on everything from a quick TMR joystick upgrade to fix stick drift, to full custom DualSense builds with cosmetic shells, mouse click triggers, and back paddles.',
    proximityNote:
      'We offer local pickup and dropoff by appointment in Temecula. No shipping required for area residents.',
    population: '110,000+',
    driveMinutes: '0–15',
    nearbyLandmarks: [
      'Promenade Temecula',
      'Old Town Temecula',
      'Pechanga Resort',
      'Wine Country',
    ],
    nearbyCities: ['Murrieta', 'Wildomar', 'Lake Elsinore', 'Fallbrook', 'Winchester'],
    lat: 33.4936,
    lng: -117.1484,
    googleBusinessUrl: 'https://maps.app.goo.gl/bbZsQWtNbftwZ3ZP8?g_st=ic',
  },
  {
    slug: 'murrieta',
    city: 'Murrieta',
    state: 'CA',
    county: 'Riverside County',
    metaTitle: 'Custom PS5 Controllers & Stick Drift Repair in Murrieta, CA | GameFix Solutions',
    metaDescription:
      'Murrieta gamers: get your PS5 DualSense upgraded with TMR hall-effect joysticks, mouse click triggers, and custom shells. Easy drop-off in nearby Temecula, or ship it to us from anywhere.',
    keywords: [
      'ps5 repair murrieta',
      'custom ps5 controller murrieta',
      'ps5 stick drift murrieta',
      'murrieta gaming controller',
      'dualsense upgrade murrieta ca',
      'controller repair near murrieta',
      'ps5 controller upgrade murrieta',
    ],
    heroHeading: 'Custom PS5 Controllers & Repair Near Murrieta, CA',
    heroSubheading:
      'Murrieta\'s closest professional PS5 controller upgrade shop. TMR joysticks, mouse click triggers, back paddles, and full custom builds — just minutes from California Oaks.',
    localIntro:
      'Murrieta is one of the fastest-growing cities in Southern California, and we\'ve built controllers for players across the California Oaks, Bear Creek, and Greer Ranch neighborhoods. We\'re based just south in Temecula, making us an easy drive for Murrieta residents who want professional-grade controller modifications without waiting weeks for a manufacturer return.',
    proximityNote:
      'Temecula pickup is typically 10–15 minutes from most Murrieta neighborhoods. Mail-in is also available if you prefer to skip the drive.',
    population: '120,000+',
    driveMinutes: '10–15',
    nearbyLandmarks: [
      'Town Square Murrieta',
      'California Oaks Sports Park',
      'Murrieta Hot Springs',
      'Bear Creek Golf Club',
    ],
    nearbyCities: ['Temecula', 'Wildomar', 'Lake Elsinore', 'Winchester'],
    lat: 33.5539,
    lng: -117.2139,
  },
  {
    slug: 'lake-elsinore',
    city: 'Lake Elsinore',
    state: 'CA',
    county: 'Riverside County',
    metaTitle: 'PS5 Controller Repair & Custom Builds Near Lake Elsinore, CA | GameFix Solutions',
    metaDescription:
      'Lake Elsinore gamers: professional PS5 stick drift repair, TMR joystick upgrades, and custom DualSense builds. About 20 minutes to Temecula pickup, or use our nationwide mail-in service.',
    keywords: [
      'ps5 repair lake elsinore',
      'custom ps5 controller lake elsinore',
      'ps5 stick drift lake elsinore',
      'lake elsinore gaming controller',
      'controller repair near lake elsinore',
      'dualsense upgrade lake elsinore',
    ],
    heroHeading: 'Custom PS5 Controllers & Repair Near Lake Elsinore, CA',
    heroSubheading:
      'Lake Elsinore players don\'t have to settle for stick drift or a stock controller. Professional TMR upgrades and custom DualSense builds — about 20 minutes from your front door.',
    localIntro:
      'Lake Elsinore\'s gaming community has been one of our fastest-growing customer bases. Players from the Canyon Hills, Tuscany Hills, and lakeside areas regularly make the 20-minute drive to Temecula for custom controller builds and stick drift fixes that San Diego or Riverside shops can\'t match for turnaround speed. If you\'d rather not drive, our mail-in service gets your controller back in 2–4 business days regardless of where you live.',
    proximityNote:
      'Temecula pickup is about 20 minutes from most Lake Elsinore neighborhoods. Mail-in is also available — return shipping included.',
    population: '75,000+',
    driveMinutes: '20',
    nearbyLandmarks: [
      'Lake Elsinore Storm Stadium',
      'Canyon Hills',
      'Lakeside High School area',
      'Diamond Valley Lake',
    ],
    nearbyCities: ['Temecula', 'Murrieta', 'Wildomar', 'Winchester'],
    lat: 33.6682,
    lng: -117.3273,
  },
  {
    slug: 'wildomar',
    city: 'Wildomar',
    state: 'CA',
    county: 'Riverside County',
    metaTitle: 'PS5 Controller Upgrades Near Wildomar, CA | GameFix Solutions',
    metaDescription:
      'Wildomar gamers: professional PS5 controller upgrades including TMR joystick replacement, stick drift repair, mouse click triggers, and custom builds. Minutes from Temecula pickup.',
    keywords: [
      'ps5 repair wildomar',
      'custom ps5 controller wildomar',
      'ps5 stick drift wildomar',
      'wildomar gaming controller repair',
      'controller upgrade near wildomar ca',
    ],
    heroHeading: 'Custom PS5 Controllers & Repair Near Wildomar, CA',
    heroSubheading:
      'Wildomar\'s closest professional controller modification shop. Just minutes to Temecula for TMR joystick upgrades, mouse click triggers, and full custom DualSense builds.',
    localIntro:
      'Wildomar sits between Murrieta and Lake Elsinore — which means Temecula is practically in your backyard. Players from the Wildwood, The Farm, and Clinton Keith corridor regularly use our shop for stick drift repairs and competitive controller builds. We know time matters, which is why local jobs typically turn around in 2–4 days, and mail-in returns are just as fast.',
    proximityNote:
      'Temecula pickup is 10–15 minutes from most Wildomar neighborhoods. Likely the closest professional controller shop to you.',
    population: '36,000+',
    driveMinutes: '10–15',
    nearbyLandmarks: [
      'The Farm neighborhood',
      'Clinton Keith corridor',
      'Wildwood community',
    ],
    nearbyCities: ['Murrieta', 'Temecula', 'Lake Elsinore'],
    lat: 33.5989,
    lng: -117.2806,
  },
  {
    slug: 'fallbrook',
    city: 'Fallbrook',
    state: 'CA',
    county: 'San Diego County',
    metaTitle: 'PS5 Controller Repair & Custom Builds Near Fallbrook, CA | GameFix Solutions',
    metaDescription:
      'Fallbrook gamers: professional PS5 stick drift repair, TMR joystick upgrades, and custom DualSense builds. About 25 minutes to Temecula pickup, or use our fast mail-in service.',
    keywords: [
      'ps5 repair fallbrook',
      'custom ps5 controller fallbrook',
      'ps5 stick drift fallbrook',
      'fallbrook gaming controller',
      'controller repair near fallbrook ca',
      'dualsense upgrade fallbrook',
    ],
    heroHeading: 'Custom PS5 Controllers & Repair Near Fallbrook, CA',
    heroSubheading:
      'Fallbrook\'s closest professional PS5 modification shop is in Temecula — about 25 minutes north. TMR joysticks, mouse click triggers, back paddles, and custom shells.',
    localIntro:
      'Fallbrook is San Diego County\'s northernmost community, which makes it closer to Temecula than to most San Diego shops. We serve Fallbrook players who need stick drift fixed, want a competitive controller upgrade, or are building a custom DualSense from scratch. The drive to Temecula is straightforward on the 15, or skip it entirely with our mail-in service — we send your controller back the same week.',
    proximityNote:
      'Temecula pickup is about 25 minutes from central Fallbrook via I-15. Mail-in available if you prefer — return shipping included.',
    population: '33,000+',
    driveMinutes: '25',
    nearbyLandmarks: [
      'Fallbrook Avocado Festival grounds',
      'Morro Hills area',
      'Rainbow Valley',
      'I-15 / Mission Road corridor',
    ],
    nearbyCities: ['Temecula', 'Murrieta', 'Bonsall'],
    lat: 33.3731,
    lng: -117.2503,
  },
  {
    slug: 'winchester',
    city: 'Winchester',
    state: 'CA',
    county: 'Riverside County',
    metaTitle: 'PS5 Controller Repair & Upgrades Near Winchester, CA | GameFix Solutions',
    metaDescription:
      'Winchester, CA gamers: professional PS5 TMR joystick upgrades, stick drift repair, mouse click triggers, and custom DualSense builds. Close to Temecula pickup, or mail it in from home.',
    keywords: [
      'ps5 repair winchester ca',
      'custom ps5 controller winchester',
      'ps5 stick drift winchester',
      'winchester gaming controller repair',
      'controller upgrade near winchester california',
    ],
    heroHeading: 'Custom PS5 Controllers & Repair Near Winchester, CA',
    heroSubheading:
      'Winchester gamers: professional PS5 stick drift repair and custom controller builds just south in Temecula. Fast local turnaround, or nationwide mail-in.',
    localIntro:
      'Winchester is a rapidly growing community in central Riverside County, and residents have easy access to Temecula via Winchester Road and I-15. We build and upgrade PS5 controllers for players in the French Valley, Rancho Bella Vista, and surrounding developments. Whether you\'re dealing with DualSense stick drift for the first time or building a full competitive setup with mouse click triggers and back paddles, we\'ll have it done faster than any online service.',
    proximityNote:
      'Temecula pickup is 15–20 minutes from most Winchester neighborhoods via Winchester Road. Mail-in also available.',
    population: '20,000+',
    driveMinutes: '15–20',
    nearbyLandmarks: [
      'French Valley Airport area',
      'Rancho Bella Vista',
      'Winchester Road / I-15',
      'Diamond Valley Lake',
    ],
    nearbyCities: ['Murrieta', 'Temecula', 'Hemet', 'Lake Elsinore'],
    lat: 33.6995,
    lng: -117.0864,
  },
];

export const getAreaBySlug = (slug: string): ServiceArea | undefined =>
  SERVICE_AREAS.find(a => a.slug === slug);
